import type { FC } from 'react';
import { memo, useCallback, useRef } from 'react';
import isEqual from 'lodash/isEqual';

import { addUFOCustomData } from '@atlaskit/react-ufo/add-ufo-custom-data';
import { addUFOCustomMark } from '@atlaskit/react-ufo/add-ufo-custom-mark';

import { getUniquePageLoadId } from '@confluence/unique-page-load-id/entry-points/UniquePageLoadId.ts';
import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';

import { usePerformanceAction } from '../usePerformanceAction.tsx';
import { PAGE_SEGMENT_MARK_PREFIX_END } from '../metric-keys/index.ts';
import { browserMetricsState } from '../utils/index.ts';
import { getViewTransitionSource } from '../viewTransitionSource.ts';
import type { Props } from '../types/PageSegmentLoadEndProps.ts';
import { getPageSegmentUFOBinding, reportPageSegmentEndToUFO } from '../pageSegmentUFOInterop.ts';

import { addCustomTimingForMetric } from './addCustomTimingsFromMarks.ts';
import { useIsEmbeddedTracking } from './EmbeddedConfluenceTrackingContext.tsx';

const noop = () => {};
const EMBEDDED_PREFIX = 'embedded-';

const PageSegmentLoadEndComponent: FC<Props> = memo((props) => {
	const {
		customData = {},
		onComplete = noop,
		stopTime,
		timing,
		augmentCustomDataAfterComplete,
	} = props;
	const isEmbedded = useIsEmbeddedTracking();
	// The discriminated union of Props guarantees that exactly one of `metricKey` or `metric`
	// is provided, so resolvedKey is always a defined string at runtime.
	const resolvedKey: string = props.metricKey ?? props.metric.key;
	const prefixedKey = isEmbedded ? `${EMBEDDED_PREFIX}${resolvedKey}` : resolvedKey;
	// Capture before onComplete or a deferred paint callback can start another interaction.
	const binding = props.metric ? getPageSegmentUFOBinding(props.metric) : undefined;

	const stopTimeRef = useRef(stopTime);
	const customDataRef = useRef(customData);
	const augmentCustomDataAfterCompleteRef = useRef(augmentCustomDataAfterComplete);

	stopTimeRef.current = stopTime;
	customDataRef.current = customData;
	augmentCustomDataAfterCompleteRef.current = augmentCustomDataAfterComplete;

	// Intentional use that won't cause a hydration error
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		SSRMeasures.markMeasureEnd(`ssr-app/render/${prefixedKey}`);
	}

	const handleSegmentLoadEnd = useCallback(() => {
		onComplete();
		let finalCustomData: Record<string, any>;
		if (augmentCustomDataAfterCompleteRef.current) {
			finalCustomData = augmentCustomDataAfterCompleteRef.current(customDataRef.current);
		} else {
			finalCustomData = customDataRef.current;
		}

		const data = {
			...finalCustomData,
			viewTransitionSource: getViewTransitionSource(),
			...(typeof stopTimeRef.current === 'number' ? { stopTime: stopTimeRef.current } : {}),
			previousRoute: browserMetricsState.previousRoute,
			previousContentId: browserMetricsState.previousContentId,
			pageLoadInfo: getUniquePageLoadId(),
		};
		// An imperative start has no embedded context; retain its captured key and owner.
		if (binding && (binding.key === prefixedKey || binding.key === resolvedKey)) {
			// Preserve React end-mark timing; stopTime is metadata on this API.
			reportPageSegmentEndToUFO(binding, { customData: data });
		} else {
			addUFOCustomMark(`${PAGE_SEGMENT_MARK_PREFIX_END}:${prefixedKey}`);
			addCustomTimingForMetric(prefixedKey);
			addUFOCustomData(data);
		}
	}, [binding, onComplete, prefixedKey, resolvedKey]);

	usePerformanceAction(handleSegmentLoadEnd, timing);
	return null;
}, isEqual);

export const PageSegmentLoadEnd: FC<Props> = withTransparentErrorBoundary({
	attribution: Attribution.BACKBONE,
})(PageSegmentLoadEndComponent);
